<template>
  <div
    class="navbar"
    :class="{ 'navbar--dark': isDark }"
  >
    <hamburger
      id="hamburger-container"
      :is-active="sidebar?.opened"
      class="hamburger-container"
      @toggle-click="toggleSideBar"
    />

    <!-- Search -->
    <div
      v-if="!isExtranet()"
      class="left-zone"
    >
      <div
        class="search-wrapper"
        @keydown.enter.capture="onSearchEnter"
      >
        <el-autocomplete
          v-if="
            checkPerms(['browse comptes']) ||
              checkPerms(['browse all invoices'])
          "
          v-model="query.keyword"
          class="left-menu-search"
          :fetch-suggestions="letSearch"
          highlight-first-item
          @select="selectAutocomplete"
        >
          <template #prefix>
            <magnify-icon :size="20" />
          </template>
          <template #append>
            <el-select
              v-model="query.select"
              style="width:100px"
              @change="focusInput"
            >
              <el-option
                v-if="checkPerms(['browse comptes'])"
                label="Client"
                value="cli"
              />
              <el-option
                v-if="checkPerms(['browse all invoices'])"
                label="Facture"
                value="fact"
              />
            </el-select>
          </template>

          <template #default="{ item }">
            <router-link
              :to="`/account/show/${item.id}`"
              class="autocomplete-item"
            >
              <template v-if="query.select === 'cli'">
                <div class="autocomplete-row">
                  <span class="autocomplete-name">
                    <template v-if="item.parent_nom">
                      <span class="parent-name">{{ item.parent_nom }} ›</span>
                    </template>
                    <span
                      v-html="
                        highlightSearch(item.nom_aff)
                      "
                    />
                  </span>
                  <span
                    v-if="shouldShowMatch(item)"
                    class="match-info"
                  >
                    <span
                      class="match-value"
                      v-html="
                        highlightSearch(
                          getMatchingValue(item)
                        )
                      "
                    />
                    <span
                      v-if="getMatchSourceLabel(item)"
                      class="match-type"
                    >({{
                      getMatchSourceLabel(item)
                    }})</span>
                  </span>
                </div>
              </template>
              <span v-else>
                Facture : {{ item.nav_bar_nom_aff }}
                <el-divider direction="vertical" />
                {{ item.nom_aff }}
              </span>
            </router-link>
          </template>
        </el-autocomplete>
      </div>
    </div>

    <Breadcrumb class="breadcrumb-container" />

    <!-- Right menu -->
    <div class="right-menu">
      <!-- Indicateur de géocodage en cours -->
      <div
        v-if="geocodingRunning"
        class="geocoding-indicator right-menu-item"
        @click="goToCarteClients"
      >
        <Icon
          icon="mdi:map-marker-radius"
          :width="18"
          class="geocoding-icon"
        />
        <span class="geocoding-text">{{ geocodingStatus }}</span>
        <el-progress
          :percentage="geocodingProgress"
          :show-text="false"
          :stroke-width="3"
          class="geocoding-progress"
        />
      </div>

      <NotificationBell
        v-if="!isExtranet()"
        class="right-menu-item"
      />

      <el-tooltip
        v-if="documentationDisponible"
        content="Documentation"
        placement="bottom"
      >
        <el-button
          class="right-menu-item hover-effect fullscreen-btn"
          circle
          @click="ouvrirAide"
        >
          <Icon
            icon="mdi:help-circle-outline"
            :width="22"
          />
        </el-button>
      </el-tooltip>

      <el-tooltip
        content="Notes de version"
        placement="bottom"
      >
        <el-button
          class="right-menu-item hover-effect fullscreen-btn"
          circle
          @click="openReleaseNotes"
        >
          <Icon
            icon="mdi:newspaper-variant-outline"
            :width="22"
          />
        </el-button>
      </el-tooltip>

      <ThemeToggle class="right-menu-item" />

      <el-button
        class="right-menu-item hover-effect fullscreen-btn"
        circle
        @click="toggleFullScreen"
      >
        <fullscreen-icon :size="24" />
      </el-button>

      <el-dropdown trigger="hover">
        <template #default>
          <el-button
            class="right-menu-item hover-effect fullscreen-btn"
            circle
          >
            <template v-if="userAvatar">
              <img
                :src="userAvatar"
                alt="avatar"
                class="user-avatar"
              >
            </template>
            <template v-else>
              <Icon
                icon="mdi:account"
                :width="24"
                class="user-icon"
              />
            </template>
          </el-button>
        </template>

        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item disabled>
              <b>{{ prenom }} {{ nom }}</b>
            </el-dropdown-item>

            <el-dropdown-item
              v-if="canViewProfile"
              divided
              @click="goProfile"
            >
              Profil
            </el-dropdown-item>

            <el-dropdown-item
              divided
              @click="logout"
            >
              Déconnexion
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>

    <!-- Release Notes Dialog -->
    <ReleaseNotesDialog
      v-model="showReleaseNotes"
      :releases="releaseList"
      :scope="isExtranet() ? 'extranet' : 'internal'"
    />
  </div>
</template>

<script setup>
import { ref, computed, getCurrentInstance, onMounted } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";

import Breadcrumb from "@/components/Breadcrumb";
import Hamburger from "@/components/Hamburger";
import ThemeToggle from "@/components/ThemeToggle/index.vue";
import Resource from "@/api/resource";
import { useTheme } from "@/composables/useTheme";
import { useGeocoding } from "@/composables/useGeocoding";
import { Icon } from '@iconify/vue';
import ReleaseNotesDialog from "@/components/ReleaseNotes/ReleaseNotesDialog.vue";
import NotificationBell from "@/components/NotificationBell/index.vue";
import { releases, getUnseenReleases, filterReleasesForScope } from "@/config/releases";
import { useDocumentationCatalog } from "@/composables/useDocumentationCatalog";

const { isDark } = useTheme();
const {
    isRunning: geocodingRunning,
    progress: geocodingProgress,
    statusText: geocodingStatus
} = useGeocoding();

// Release notes
const showReleaseNotes = ref(false);
const releaseList = ref([]);

const openReleaseNotes = () => {
    releaseList.value = releases;
    showReleaseNotes.value = true;
};

const checkUnseenReleases = () => {
    // L'utilisateur a pu ouvrir les notes à la main avant l'échéance du timer :
    // remplacer la liste sous ses yeux viderait la fenêtre.
    if (showReleaseNotes.value) return;

    const unseen = getUnseenReleases();
    // Un extranet client dont toutes les nouveautés sont internes n'a rien à lire :
    // ouvrir la fenêtre lui afficherait un corps vide.
    const scope = isExtranet() ? "extranet" : "internal";
    if (filterReleasesForScope(unseen, scope).length > 0) {
        releaseList.value = unseen;
        showReleaseNotes.value = true;
    }
};

const goToCarteClients = () => {
    router.push("/comptes/carte");
};

// ─── Documentation ───────────────────────────────────────
// Accès général aux guides. L'aide de l'écran consulté, elle, vit dans
// l'en-tête de la page (PageHeader), au plus près de ce qu'elle documente.
// Le catalogue est déjà filtré par le serveur : s'il revient vide, ce compte
// n'a aucun guide à lire et le bouton n'a pas lieu d'être.
const {
    guides: documentationGuides,
    load: chargerDocumentation,
    reset: resetDocumentation
} = useDocumentationCatalog();

const documentationDisponible = computed(() => documentationGuides.value.length > 0);

const ouvrirAide = () => router.push({ name: "documentation" });

const store = useStore();
const router = useRouter();
const { proxy } = getCurrentInstance();

// Côté client calculé par le serveur : le nom du rôle ne le désigne plus.
const isExtranet = proxy.$isExtranet;
const checkPerms = proxy.$checkPerms;
const sidebar = computed(() => store.getters.sidebar);
const prenom = computed(() => store.state.user.prenom || "Utilisateur");
const nom = computed(() => store.state.user.name || "");
const userAvatar = computed(() => store.state.user.avatarUrl); // ou autre champ
const canViewProfile = computed(() => true); // Tout utilisateur authentifié peut voir son profil

const query = ref({
    page: 1,
    limit: 50,
    keyword: "",
    select: "",
    showActive: [1]
});

const inputRef = ref(null);
// Méthodes
const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
    } else if (document.exitFullscreen) {
        document.exitFullscreen();
    }
};
const focusInput = () => inputRef.value?.focus();

const toggleSideBar = () => store.dispatch("app/toggleSideBar");

const logout = async () => {
    await store.dispatch("user/logout");
    // Le catalogue dépend des droits : le compte suivant ne doit pas hériter
    // de la liste de guides du précédent.
    resetDocumentation();
    router.push("/login");
};

const goProfile = () => {
    router.push("/profile/edit");
};

/* search */
const letSearch = async (_, cb) => {
    const keyword = query.value.keyword?.toLowerCase() || "";
    if (!keyword) return cb([]);

    // Mode facture : recherche sur l'endpoint comptes avec select=fact
    if (query.value.select === "fact") {
        const factResource = new Resource("comptes");
        const searchQuery = { ...query.value, limit: 5000, orderbysens: 'desc' };
        const { data } = await factResource.list(searchQuery);
        // Ajouter la propriété value requise par el-autocomplete
        const results = (data || []).map(item => ({
            ...item,
            value: item.nav_bar_nom_aff || item.nom_aff || String(item.id)
        }));
        return cb(results);
    }

    // Mode client : recherche classique sur comptes
    const cliResource = new Resource("comptes");
    const searchQuery = { ...query.value, with_childs: true };
    const { data } = await cliResource.list(searchQuery);

    // Aplatir les résultats : si un enfant matche, l'afficher directement
    const flatResults = [];

    // Calcule un score de pertinence pour trier les résultats
    const computeRelevanceScore = name => {
        if (!name || !keyword) return 0;
        const lower = name.toLowerCase();
        if (lower === keyword) return 100;
        if (lower.startsWith(keyword)) return 80;
        if (lower.includes(keyword)) return 60;
        const keywordWords = keyword.split(/\s+/);
        const allWordsMatch = keywordWords.every(w => lower.includes(w));
        if (allWordsMatch) return 40;
        return 20;
    };

    data.forEach(item => {
        const children = item.children || item.enfants || [];
        const search = item.search || {};

        // Le parent s'affiche SEULEMENT s'il a un match propre (pas juste via ses enfants)
        const nameMatches =
            item.nom_aff && item.nom_aff.toLowerCase().includes(keyword);
        const hasOwnMatch =
            search.own_match_source && search.own_match_source !== null;

        if (nameMatches || hasOwnMatch) {
            flatResults.push({
                ...item,
                value: item.nom_aff || String(item.id),
                _relevance: computeRelevanceScore(item.nom_aff)
            });
        }

        // Ajouter les enfants qui matchent
        const matchingChildrenInfo = search.matching_children || [];
        children.forEach(child => {
            if (child.search?.matches_search) {
                const childMatchInfo =
                    matchingChildrenInfo.find(mc => mc.id === child.id) || {};
                flatResults.push({
                    ...child,
                    parent_nom: item.nom_aff,
                    value: child.nom_aff || String(child.id),
                    _relevance: computeRelevanceScore(child.nom_aff),
                    search: {
                        ...child.search,
                        matching_value:
                            child.search.matching_value ||
                            childMatchInfo.matching_value,
                        matching_contact_name:
                            child.search.matching_contact_name ||
                            childMatchInfo.matching_contact_name,
                        match_source:
                            child.search.match_source ||
                            childMatchInfo.match_source
                    }
                });
            }
        });
    });

    // Pré-calculer le score max par groupe (famille)
    const groupMaxScore = new Map();
    flatResults.forEach(item => {
        const groupKey = item.id_parent || item.id;
        const current = groupMaxScore.get(groupKey) || 0;
        if (item._relevance > current)
            groupMaxScore.set(groupKey, item._relevance);
    });

    // Trier par pertinence décroissante puis alphabétiquement, en respectant la hiérarchie
    flatResults.sort((a, b) => {
        const groupA = a.id_parent || a.id;
        const groupB = b.id_parent || b.id;

        if (groupA === groupB) {
            const aIsParent = !a.id_parent;
            const bIsParent = !b.id_parent;
            if (aIsParent !== bIsParent) return aIsParent ? -1 : 1;
            if (b._relevance !== a._relevance)
                return b._relevance - a._relevance;
            return (a.nom_aff || "").localeCompare(b.nom_aff || "");
        }

        const maxA = groupMaxScore.get(groupA) || 0;
        const maxB = groupMaxScore.get(groupB) || 0;
        if (maxA !== maxB) return maxB - maxA;
        return (a.nom_aff || "").localeCompare(b.nom_aff || "");
    });

    cb(flatResults);
};

/* Fonctions pour afficher les informations de match */
const getMatchInfo = item => {
    if (item.search) {
        return item.search.own_match_source || item.search.match_source;
    }
    return item.own_match_source || item.match_source;
};

const getMatchingValue = item => {
    const search = item.search || item;
    const keyword = query.value.keyword?.toLowerCase() || "";

    // Si matching_value contient le keyword, l'utiliser
    if (
        search.matching_value &&
        String(search.matching_value)
            .toLowerCase()
            .includes(keyword)
    ) {
        return search.matching_value;
    }

    // Sinon, si c'est un match contact, utiliser matching_contact_name
    if (
        search.matching_contact_name &&
        String(search.matching_contact_name)
            .toLowerCase()
            .includes(keyword)
    ) {
        return search.matching_contact_name;
    }

    // Fallback
    return search.matching_value || search.matching_contact_name;
};

const getMatchSourceLabel = item => {
    const source = getMatchInfo(item);
    const labels = {
        nom: "Nom",
        raison: "Raison sociale",
        alias: "Alias",
        mail: "Email",
        contact: "Contact",
        tel: "Tél",
        ville: "Ville",
        siret: "SIRET",
        service: "Service",
        siren: "SIREN"
    };
    return labels[source] || "";
};

const shouldShowMatch = item => {
    const matchingValue = getMatchingValue(item);
    const source = getMatchInfo(item);

    // Pas de valeur à afficher
    if (!matchingValue) return false;

    // Si c'est un match sur le nom/raison sociale, pas besoin de le répéter
    if (
        source === "nom" ||
        source === "raison" ||
        source === "raison_sociale" ||
        source === "name"
    ) {
        return false;
    }

    // L'alias se compare normalisé (« st-aignan » trouve « SAINT AIGNAN ») :
    // il ne contient pas forcément la saisie telle quelle, mais c'est lui qui
    // explique pourquoi le compte remonte.
    if (source === "alias") return true;

    // Vérifier que la valeur contient bien le terme recherché
    const keyword = query.value.keyword?.toLowerCase();
    if (!keyword || keyword.length < 2) return false;

    return String(matchingValue)
        .toLowerCase()
        .includes(keyword);
};

const escapeHtml = value =>
    String(value).replace(
        /[&<>"']/g,
        c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]
    );

// Le résultat part en v-html : noms, mails et alias sont des saisies libres.
// Le texte brut est découpé autour des correspondances, puis chaque morceau est
// échappé — surligner après échappement couperait les entités (« amp », « lt »).
const highlightSearch = text => {
    if (!text) {
        return text;
    }
    const keyword = query.value.keyword;
    if (!keyword || keyword.length < 2) {
        return escapeHtml(text);
    }
    const regex = new RegExp(
        `(${keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
        "gi"
    );
    return String(text)
        .split(regex)
        .map((morceau, i) =>
            i % 2 === 1
                ? `<mark class="search-highlight">${escapeHtml(morceau)}</mark>`
                : escapeHtml(morceau)
        )
        .join("");
};

const onSearchEnter = e => {
    const keyword = query.value.keyword?.trim();
    if (keyword && /^\d+$/.test(keyword)) {
        e.stopPropagation();
        e.preventDefault();
        router.push(`/account/show/${keyword}`);
        query.value.keyword = "";
    }
};

const selectAutocomplete = item => {
    router.push(`/account/show/${item.id}`);
    query.value.keyword = "";
};

const pickDefaultSelect = (permCli, permFact) => {
    if (checkPerms([permCli]) && !checkPerms([permFact])) return "cli";
    if (checkPerms([permFact]) && !checkPerms([permCli])) return "fact";
    if (checkPerms([permCli]) && checkPerms([permFact])) return "cli";
    return "";
};

onMounted(() => {
    // Auto-afficher les release notes non vues après un court délai
    setTimeout(() => {
        checkUnseenReleases();
    }, 1500);

    // Catalogue des guides : une requête par session, partagée avec l'écran
    // Documentation. Un échec laisse simplement le bouton d'aide masqué.
    chargerDocumentation().catch(() => {});

    query.value.select = isExtranet()
        ? pickDefaultSelect(
              "extranet browse all comptes",
              "extranet browse all invoices"
          )
        : pickDefaultSelect("browse comptes", "browse all invoices");
});
</script>

<!-- eslint-disable -->

<style lang="scss" scoped>
.navbar {
    height: 50px;
    background-color: #ffffff;
    box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
    display: flex;
    align-items: center;
    padding: 0 10px;
    transition: background-color 0.3s, box-shadow 0.3s;
}

/* Dark mode navbar - bleu foncé de marque */
.navbar--dark {
    background-color: #02264a !important;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3) !important;
}

.hamburger-container {
    height: 100%;
    display: flex;
    align-items: center;
    cursor: pointer;

    &:hover {
        background: rgba(0, 0, 0, 0.025);
    }
}

.left-zone {
    margin-left: 10px;
}

.left-menu-search {
    width: 350px;

    :deep(.el-input__wrapper) {
        border-radius: 8px 0 0 8px;
        background-color: #f5f7fa;
        box-shadow: none;
        border: 1px solid #dcdfe6;
        border-right: none;
        transition: all 0.2s;

        &:hover,
        &:focus,
        &.is-focus {
            box-shadow: none;
            border-color: #6ed6ce;
        }
    }

    :deep(.el-input-group__append) {
        border-radius: 0 8px 8px 0;
        background-color: #f5f7fa;
        border: 1px solid #dcdfe6;
        border-left: none;
        box-shadow: none;
        padding: 0;

        .el-select {
            margin: 0;

            .el-input__wrapper {
                border: none;
                background: transparent;
                border-radius: 0 8px 8px 0;
                padding: 0 12px;
                box-shadow: none !important;
            }
        }
    }
}

.breadcrumb-container {
    margin-left: 15px;
}

:deep(.el-dropdown-menu__item.is-disabled) {
    color: #000 !important;
    cursor: default;
    opacity: 1;
}

.right-menu {
    display: flex;
    align-items: center;
    margin-left: auto;
}

.right-menu-item {
    margin-left: 8px;
    cursor: pointer;
}

.autocomplete-item {
    display: block;
    width: 100%;
}

.autocomplete-row {
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    overflow: hidden;
}

.autocomplete-name {
    flex-shrink: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
}

.parent-name {
    color: #909399;
}

.match-info {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    margin-left: auto;
}

.match-value {
    color: #606266;
}

.match-type {
    color: #606266;
    font-weight: 600;
    font-size: 13px;
}

:deep(.search-highlight) {
    background-color: #fef08a;
    color: #92400e;
    padding: 1px 2px;
    border-radius: 2px;
    font-weight: 600;
}

.fullscreen-btn {
    background-color: transparent;
    border: none;
    padding: 0;
}

.fullscreen-btn:hover {
    background-color: rgba(0, 0, 0, 0.05); // léger hover
}

.user-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
}

.user-icon {
    color: #606266;
}

/* Dark mode styles additionnels */
.navbar--dark .hamburger-container:hover {
    background: rgba(255, 255, 255, 0.1);
}

.navbar--dark .fullscreen-btn:hover {
    background-color: rgba(255, 255, 255, 0.1);
}

.navbar--dark .user-icon {
    color: #a3a6ad;
}

.navbar--dark .parent-name {
    color: #a3a6ad;
}

.navbar--dark .match-value,
.navbar--dark .match-type {
    color: #a3a6ad;
}

/* Indicateur de géocodage */
.geocoding-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 12px;
    background-color: rgba(110, 214, 206, 0.15);
    border-radius: 16px;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background-color: rgba(110, 214, 206, 0.25);
    }
}

.geocoding-icon {
    color: #6ed6ce;
    animation: pulse 1.5s ease-in-out infinite;
}

.geocoding-text {
    font-size: 12px;
    color: #02264a;
    white-space: nowrap;
}

.geocoding-progress {
    width: 60px;
}

@keyframes pulse {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
}

/* Dark mode geocoding indicator */
.navbar--dark .geocoding-indicator {
    background-color: rgba(110, 214, 206, 0.2);

    &:hover {
        background-color: rgba(110, 214, 206, 0.3);
    }
}

.navbar--dark .geocoding-text {
    color: #ffffff;
}

/* Dark mode search bar - unified with wrapper */
.navbar--dark .search-wrapper {
    background-color: #1e2837;
    border: 1px solid rgba(110, 214, 206, 0.35);
    border-radius: 8px;
    display: inline-flex;
    overflow: hidden;

    &:hover {
        border-color: rgba(110, 214, 206, 0.6);
    }
}

.navbar--dark .search-wrapper .left-menu-search {
    :deep(.el-input__wrapper) {
        background-color: transparent !important;
        background: transparent !important;
        border: none !important;
        border-width: 0 !important;
        border-radius: 0 !important;
        box-shadow: none !important;
    }

    :deep(.el-input__inner) {
        color: #ffffff !important;
        background: transparent !important;

        &::placeholder {
            color: rgba(255, 255, 255, 0.45) !important;
        }
    }

    :deep(.el-input__prefix) {
        color: rgba(255, 255, 255, 0.6) !important;
    }

    :deep(.el-input-group__append) {
        background-color: transparent !important;
        background: transparent !important;
        border: none !important;
        border-width: 0 !important;
        border-left: none !important;
        box-shadow: none !important;
        margin: 0 !important;
        padding: 0 !important;
    }

    :deep(.el-input-group__append .el-select .el-input__wrapper) {
        background: transparent !important;
        border: none !important;
        border-width: 0 !important;
        box-shadow: none !important;
        border-radius: 0 !important;
    }

    :deep(.el-input-group__append .el-select .el-input__inner) {
        color: rgba(255, 255, 255, 0.8) !important;
    }

    :deep(.el-input-group__append .el-select .el-input__suffix) {
        color: rgba(255, 255, 255, 0.5) !important;
    }
}
</style>

<style>
/* Global overrides for dark mode search bar - non-scoped for higher specificity */
.navbar--dark .search-wrapper .el-input__wrapper,
.navbar--dark
    .search-wrapper
    .el-input-group__append
    .el-select
    .el-input__wrapper,
.navbar--dark .search-wrapper .el-select .el-input__wrapper,
.navbar--dark .search-wrapper .el-select .el-input .el-input__wrapper {
    background-color: transparent !important;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
}

/* Style the append section - seamless unified look */
.navbar--dark .search-wrapper .el-input-group__append {
    background-color: transparent !important;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
    padding: 0 8px 0 0 !important;
    margin: 0 !important;
}

/* Target the select input specifically - all states */
.navbar--dark .search-wrapper .el-select,
.navbar--dark .search-wrapper .el-select .el-input,
.navbar--dark .search-wrapper .el-select .el-input__wrapper,
.navbar--dark .search-wrapper .el-select .el-input__wrapper:hover,
.navbar--dark .search-wrapper .el-select .el-input__wrapper:focus,
.navbar--dark .search-wrapper .el-select .el-input__wrapper:active,
.navbar--dark .search-wrapper .el-select .el-input.is-focus .el-input__wrapper,
.navbar--dark .search-wrapper .el-select .el-input__wrapper.is-focus,
.navbar--dark .search-wrapper .el-select.is-focus .el-input__wrapper,
.navbar--dark .search-wrapper .el-select--mini .el-input__wrapper,
.navbar--dark .search-wrapper .el-select .el-select__wrapper,
.navbar--dark .search-wrapper .el-select .el-select__wrapper:hover,
.navbar--dark .search-wrapper .el-select .el-select__wrapper:focus,
.navbar--dark .search-wrapper .el-select .el-select__wrapper.is-focus,
.navbar--dark .search-wrapper .el-select .el-select__wrapper.is-hovering {
    background: transparent !important;
    background-color: transparent !important;
    border: none !important;
    border-width: 0 !important;
    border-color: transparent !important;
    box-shadow: none !important;
    outline: none !important;
}

/* Force remove any visual indicator on select focus/hover */
.navbar--dark .search-wrapper .el-select .el-input__wrapper::after,
.navbar--dark .search-wrapper .el-select .el-input__wrapper::before,
.navbar--dark .search-wrapper .el-select .el-select__wrapper::after,
.navbar--dark .search-wrapper .el-select .el-select__wrapper::before {
    display: none !important;
    content: none !important;
}

/* Adjust select text and arrow positioning - text right-aligned */
.navbar--dark .search-wrapper .el-select .el-input__wrapper {
    padding-left: 0 !important;
    padding-right: 6px !important;
    justify-content: flex-end !important;
}

.navbar--dark .search-wrapper .el-select .el-input__inner,
.navbar--dark .search-wrapper .el-select .el-select__placeholder,
.navbar--dark .search-wrapper .el-select .el-select__selected-item,
.navbar--dark .search-wrapper .el-select .el-input .el-input__inner {
    text-align: right !important;
    margin-left: auto !important;
    padding-right: 0 !important;
    line-height: 1 !important;
    padding-top: 1px !important;
}

.navbar--dark .search-wrapper .el-select .el-input__suffix {
    margin-left: 2px !important;
    margin-right: 0 !important;
    padding: 0 !important;
    flex-shrink: 0 !important;
}

/* Force le select wrapper à aligner à droite */
.navbar--dark .search-wrapper .el-select .el-select__wrapper {
    justify-content: flex-end !important;
    text-align: right !important;
}

.navbar--dark .search-wrapper .el-select .el-select__selection {
    justify-content: flex-end !important;
    margin-left: auto !important;
}
</style>
